import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { markCollabDraftFetched } from '@confluence/fabric-providers/entry-points/CollabDraftCacheManager.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	fetchCollabPermissionToken,
	nativeCollabPermissionsTokenState,
} from '@confluence/native-collab/entry-points/CollabPermissionsToken.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { preloadCollabDraftContent } from './preload/preloadNativeEditor.ts';

/*
 * The NativeEditPageDataLoader component allows for queries/components to be loaded on edit button hover.
 * Currently it fetches the NCS permissions token.
 */
export const NativeEditPageDataLoader = ({ contentId, spaceKey, contentType }) => {
	const { userId, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true)) {
			return;
		}
		// Fire and forget: Populates apollo cache
		try {
			void (async () => {
				await preloadCollabDraftContent(contentId, undefined, false);

				// Mark the timestamp after fetch (whether from cache or network)
				// so subsequent fetches within TTL can use cache
				if (expValEquals('platform_editor_collabdraft_query_improvements', 'isEnabled', true)) {
					markCollabDraftFetched(contentId, undefined);
				}
			})();
		} catch (err: any) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'CollabDraftContent preloading',
					action: 'error preloading collab draft',
					source: 'NativeEditPageDataLoader',
					attributes: {
						contentId,
						err,
					},
				},
			}).fire();
		}
	}, [contentId, createAnalyticsEvent]);

	useEffect(() => {
		void (async () => {
			const getPermissionsToken = async (): Promise<string | null> => {
				try {
					const token = await fetchCollabPermissionToken(contentId, '')();
					if (token) {
						nativeCollabPermissionsTokenState.setTimestamp(contentId);
						return token;
					}
				} catch (err: any) {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							actionSubject: 'NCS preloading',
							action: 'error preloading perms token on hover',
							source: 'NativeEditPageDataLoader',
							attributes: {
								contentId,
								err,
							},
						},
					}).fire();
				}
				return null;
			};

			nativeCollabPermissionsTokenState.setToken(contentId, getPermissionsToken());
		})();
	}, [contentId, cloudId, contentType, spaceKey, userId, createAnalyticsEvent]);

	return null;
};
